import { z } from 'zod';
import { DimensionDefinition } from 'libs/end-to-end/src/types/databases/dimensions/dimension-definition';
import { FilterSetDefinitions } from 'libs/end-to-end/src/types/databases/filters/filter-definition';
import { FocusAreaDefinition } from 'libs/end-to-end/src/types/databases/focus-areas/focus-area-definition';
import { FullCopilotDefinition } from 'libs/end-to-end/src/types/databases/copilots/copilot-definition';
import { CopilotAutoMLConfiguration } from 'libs/end-to-end/src/copilot';
import { CopilotType } from 'libs/end-to-end/src/copilot';
import { UpsellDefinition } from 'libs/end-to-end/src/types/databases/copilots/upsell-definition';
import { UpsellType } from 'libs/end-to-end/src/types/databases/copilots/upsell-definition';
import { CalibrationFraming } from 'libs/end-to-end/src/calibration-framing';
import { ColumnFilterDisallowedReason } from 'libs/end-to-end/src/column-filter-disallow-reason';
import { FilterSetDisallowedReason } from 'libs/end-to-end/src/filter-set-disallow-reason';
import { HiringNotificationEmailSubjectSuffixTemplate } from 'libs/end-to-end/src/types/hiring-notification-sender/hiring-notification-email-subject-suffix-template';
import { DynamicPipelineAnalysisConfiguration } from 'libs/end-to-end/src/dynamic-pipeline-analysis-configuration';
import { ByMetricTrendsConfig } from 'libs/end-to-end/src/types/databases/trends/by-metric-trends-config';

export const DatabaseRevisionSchemaValidationDimensionInsights = z.object({
  columnHeaderValue: z.string(),
  didChangeLevels: z.boolean(),
  isInvalid: z.boolean(),
  validationMessage: z.string().optional(),
});

export type DatabaseRevisionSchemaValidationDimensionInsights = z.infer<
  typeof DatabaseRevisionSchemaValidationDimensionInsights
>;

export const DatabaseRevisionSchemaValidationFocusAreaError = z.object({
  focusArea: z.string(),
  invalidMetrics: z.array(z.string()),
  invalidControlVariables: z.array(z.string()),
  validationMessage: z.string().optional(),
});

export type DatabaseRevisionSchemaValidationFocusAreaError = z.infer<
  typeof DatabaseRevisionSchemaValidationFocusAreaError
>;

export const DatabaseRevisionSchemaValidationFilterErrorDetailsFilterSet = z
  .object({
    reason: FilterSetDisallowedReason,
  })
  .describe('DatabaseRevisionSchemaValidationFilterErrorDetailsFilterSet');

export type DatabaseRevisionSchemaValidationFilterErrorDetailsFilterSet =
  z.infer<typeof DatabaseRevisionSchemaValidationFilterErrorDetailsFilterSet>;

export const DatabaseRevisionSchemaValidationFilterErrorDetailsFilter = z
  .object({
    columnHeaderValue: z.string(),
    reason: ColumnFilterDisallowedReason,
  })
  .describe('DatabaseRevisionSchemaValidationFilterErrorDetailsFilter');

export type DatabaseRevisionSchemaValidationFilterErrorDetailsFilter = z.infer<
  typeof DatabaseRevisionSchemaValidationFilterErrorDetailsFilter
>;

export const DatabaseRevisionSchemaValidationFilterErrorDetails = z
  .union([
    DatabaseRevisionSchemaValidationFilterErrorDetailsFilterSet,
    DatabaseRevisionSchemaValidationFilterErrorDetailsFilter,
  ])
  .describe('DatabaseRevisionSchemaValidationFilterErrorDetails');

export type DatabaseRevisionSchemaValidationFilterErrorDetails = z.infer<
  typeof DatabaseRevisionSchemaValidationFilterErrorDetails
>;

export const DatabaseRevisionSchemaValidationFilterError = z.object({
  filterSetName: z.string(),
  details: DatabaseRevisionSchemaValidationFilterErrorDetails,
});

export type DatabaseRevisionSchemaValidationFilterError = z.infer<
  typeof DatabaseRevisionSchemaValidationFilterError
>;

export const DatabaseRevisionSchemaValidationCopilotErrorReason = z.enum([
  'requires-external-dataset',
  'requires-internal-dataset',
  'requires-focus-areas',
  'requires-metrics',
  'requires-control-variables',
]);

export type DatabaseRevisionSchemaValidationCopilotErrorReason = z.infer<
  typeof DatabaseRevisionSchemaValidationCopilotErrorReason
>;

export const DatabaseRevisionSchemaValidationCopilotError = z.object({
  copilotType: CopilotType,
  reason: DatabaseRevisionSchemaValidationCopilotErrorReason,
  validationMessage: z.string().optional(),
});

export type DatabaseRevisionSchemaValidationCopilotError = z.infer<
  typeof DatabaseRevisionSchemaValidationCopilotError
>;

const DatabaseRevisionSchemaValidationCopilotAutoMLConfigurationErrorReason =
  z.enum([
    'na-column-filter-invalid',
    'selected-top-percentile-invalid',
    'huber-loss-delta-invalid',
    'robustness-penalty-invalid',
  ]);

export const DatabaseRevisionSchemaValidationCopilotAutoMLConfigurationError =
  z.object({
    reason:
      DatabaseRevisionSchemaValidationCopilotAutoMLConfigurationErrorReason,
    validationMessage: z.string().optional(),
  });

export type DatabaseRevisionSchemaValidationCopilotAutoMLConfigurationError =
  z.infer<
    typeof DatabaseRevisionSchemaValidationCopilotAutoMLConfigurationError
  >;

export const DatabaseRevisionSchemaValidationUpsellErrorReason = z.enum([
  'duplicate',
]);

export type DatabaseRevisionSchemaValidationUpsellErrorReason = z.infer<
  typeof DatabaseRevisionSchemaValidationUpsellErrorReason
>;

export const DatabaseRevisionSchemaValidationUpsellError = z.object({
  upsellType: UpsellType,
  reason: DatabaseRevisionSchemaValidationUpsellErrorReason,
});

export type DatabaseRevisionSchemaValidationUpsellError = z.infer<
  typeof DatabaseRevisionSchemaValidationUpsellError
>;

export const DatabaseRevisionSchemaValidationMiscErrorReason = z.enum([
  'invalid-override-upload-snapshot-date',
  'new-explanations-missing-required-configuration',
]);

export type DatabaseRevisionSchemaValidationMiscErrorReason = z.infer<
  typeof DatabaseRevisionSchemaValidationMiscErrorReason
>;

export const DatabaseRevisionSchemaValidationMiscError = z.object({
  reason: DatabaseRevisionSchemaValidationMiscErrorReason,
  validationMessage: z.string().optional(),
});

export type DatabaseRevisionSchemaValidationMiscError = z.infer<
  typeof DatabaseRevisionSchemaValidationMiscError
>;

export const DatabaseRevisionSchemaValidationByMetricTrendsMetricError =
  z.object({
    type: z.literal('metric'),
    metric: z.string(),
    reason: z.string(),
  });

export type DatabaseRevisionSchemaValidationByMetricTrendsMetricError = z.infer<
  typeof DatabaseRevisionSchemaValidationByMetricTrendsMetricError
>;

export const DatabaseRevisionSchemaValidationByMetricTrendsGroupByError =
  z.object({
    type: z.literal('groupBy'),
    key: z.string(),
    reason: z.string(),
  });

export type DatabaseRevisionSchemaValidationByMetricTrendsGroupByError =
  z.infer<typeof DatabaseRevisionSchemaValidationByMetricTrendsGroupByError>;

export const DatabaseRevisionSchemaValidationByMetricTrendsError = z.union([
  DatabaseRevisionSchemaValidationByMetricTrendsMetricError,
  DatabaseRevisionSchemaValidationByMetricTrendsGroupByError,
]);

export type DatabaseRevisionSchemaValidationByMetricTrendsError = z.infer<
  typeof DatabaseRevisionSchemaValidationByMetricTrendsError
>;

export const DatabaseRevisionSchemaValidationResult = z.object({
  dimensions: z.array(DimensionDefinition),
  filterSets: FilterSetDefinitions,
  focusAreas: z.array(FocusAreaDefinition),
  copilots: z.array(FullCopilotDefinition),
  upsells: z.array(UpsellDefinition),
  dimensionErrors: z.array(DatabaseRevisionSchemaValidationDimensionInsights),
  focusAreaErrors: z.array(DatabaseRevisionSchemaValidationFocusAreaError),
  copilotErrors: z.array(DatabaseRevisionSchemaValidationCopilotError),
  copilotAutoMLConfigurationErrors: z.array(
    DatabaseRevisionSchemaValidationCopilotAutoMLConfigurationError,
  ),
  filterErrors: z.array(DatabaseRevisionSchemaValidationFilterError),
  upsellErrors: z.array(DatabaseRevisionSchemaValidationUpsellError),
  groupByTrendsErrors: z.array(
    DatabaseRevisionSchemaValidationByMetricTrendsError,
  ),
  miscErrors: z.array(DatabaseRevisionSchemaValidationMiscError),
  hasErrors: z.boolean(),
  hasWarnings: z.boolean(),
  calibrationFraming: CalibrationFraming,
  showAllInsights: z.boolean(),
  showAIExplanations: z.boolean(),
  showKeyFactors: z.boolean().optional(),
  showGeolocation: z.boolean().optional(),
  showNewDataIntelligenceDeepDivePage: z.boolean(),
  showProgressPage: z.boolean(),
  showSavedAnalysesPage: z.boolean(),
  showRubricsPage: z.boolean(),
  showDownloadMultiAnalysisButton: z.boolean(),
  showDownloadSingleAnalysisButton: z.boolean(),
  showDynamicPipelineAnalysisPage: z.boolean(),
  showExplorePage: z.boolean(),
  showDashboardsPage: z.boolean(),
  showHeatmapAverages: z.boolean(),
  showCustomChartTypes: z.boolean(),
  showFobButtonInDashboardPage: z.boolean(),
  sendHiringNotificationSummaryEmailAfterTopLevelAnalysis: z
    .boolean()
    .optional(),
  hiringNotificationEmailSubjectSuffixTemplate:
    HiringNotificationEmailSubjectSuffixTemplate.optional(),
  forceHiringNotificationSummaryToOnlySendToThisEmail: z
    .string()
    .email()
    .optional(),
  numPreviousDaysOfApplicantsForHiringNotificationSummaryEmail: z
    .number()
    .min(0)
    .optional(),
  minReadinessRatingForHiringNotificationSummaryEmail: z
    .number()
    .min(1)
    .optional(),
  /**
   * For CC'ing additional emails on hiring notification summary emails, mainly
   * for debugging
   */
  additionalEmailsToSendAllHiringNotificationSummaryEmailsTo: z
    .string()
    .email()
    .array()
    .optional(),
  enableAtsWriteBack: z.boolean().optional(),
  hideFocusAreaSolutionsPage: z.boolean(),
  hideEmployeeLifecyclePage: z.boolean(),
  translationPhrasingCode: z.string(),
  overrideUploadSnapshotDate: z.string(),
  pipelineAnalysisConfiguration: z
    .array(DynamicPipelineAnalysisConfiguration)
    .optional(),
  useDisparityOnRay: z.boolean(),
  useDisparityOnParquet: z.boolean(),
  useBootstrappingOnKeyFactors: z.boolean(),
  numberOfBootstrappingRuns: z.number().int(),
  useHeatmapOnRay: z.boolean(),
  useFAOnRay: z.boolean(),
  byMetricTrendsConfiguration: ByMetricTrendsConfig,
  copilotAutoMLConfiguration: CopilotAutoMLConfiguration,
  showNewExplanations: z.boolean(),
  enablePublicDashboards: z.boolean(),
  allowInviteExternalUsersToDashboards: z.boolean(),
  showDashboardAISummaries: z.boolean(),
  showChatbot: z.boolean(),
});

export type DatabaseRevisionSchemaValidationResult = z.infer<
  typeof DatabaseRevisionSchemaValidationResult
>;
